import { useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { PLAY_STORE_URL } from "../data/aboutContent";

/**
 * Landing page for quiz / friend invite links.
 * Android / ?store=1 → Play Store; everyone else gets open-app + install CTAs.
 * Quiz query (`?quiz=`) is preserved so the app opens the shared quiz.
 */
export default function InviteLanding() {
  const { code } = useParams<{ code: string }>();
  const [searchParams] = useSearchParams();
  const inviteCode = (code ?? "").trim().toUpperCase();
  const quizId = (searchParams.get("quiz") ?? "").trim();
  const quizTitle = (searchParams.get("title") ?? "").trim();

  const deepLink = useMemo(() => {
    const params = new URLSearchParams();
    if (quizId) params.set("quiz", quizId);
    if (quizTitle) params.set("title", quizTitle);
    const qs = params.toString();
    const path = inviteCode
      ? `skutopia://invite/${encodeURIComponent(inviteCode)}`
      : "skutopia://";
    return qs ? `${path}?${qs}` : path;
  }, [inviteCode, quizId, quizTitle]);

  const shouldSendToStore = useMemo(() => {
    if (typeof window === "undefined") return false;
    const ua = navigator.userAgent || "";
    const android = /Android/i.test(ua);
    const nav = window.navigator as Navigator & { standalone?: boolean };
    const pwa =
      (window.matchMedia &&
        window.matchMedia("(display-mode: standalone)").matches) ||
      nav.standalone === true;
    const allowWeb = searchParams.get("web") === "1";
    const forceStore = searchParams.get("store") === "1";
    return (forceStore || android) && !pwa && !allowWeb;
  }, [searchParams]);

  useEffect(() => {
    if (shouldSendToStore) {
      window.location.replace(PLAY_STORE_URL);
    }
  }, [shouldSendToStore]);

  if (shouldSendToStore) {
    return (
      <main className="grid min-h-screen place-items-center bg-[#044d35] px-6 text-center text-white">
        <div>
          <p className="text-lg font-semibold">Opening Google Play…</p>
          <a
            href={PLAY_STORE_URL}
            className="mt-4 inline-block text-sm underline underline-offset-2"
          >
            Tap here if nothing happens
          </a>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#044d35] px-6 py-16 text-white">
      <div className="mx-auto max-w-lg text-center">
        <p className="text-sm font-semibold uppercase tracking-wide text-white/70">
          Skutopia Academy
        </p>
        <h1 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
          You&apos;re invited
        </h1>
        <p className="mt-4 text-base text-white/85">
          {quizTitle
            ? `A friend challenged you on “${quizTitle}”. Open the app to take the same quiz`
            : "A friend shared their quiz score and wants you to join them on Skutopia. Open the app to accept the invite"}
          {inviteCode ? (
            <>
              {" "}
              (<span className="font-mono font-semibold">{inviteCode}</span>)
            </>
          ) : null}
          .
        </p>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <a
            href={deepLink}
            className="inline-flex items-center justify-center rounded-xl bg-white px-5 py-3 text-sm font-bold text-[#044d35]"
          >
            {quizId ? "Open quiz in app" : "Open in app"}
          </a>
          <a
            href={PLAY_STORE_URL}
            className="inline-flex items-center justify-center rounded-xl border border-white/40 px-5 py-3 text-sm font-semibold text-white"
          >
            Get the app on Google Play
          </a>
        </div>
      </div>
    </main>
  );
}
